import React, { useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';

import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';
import { AnswerSource, AnswerSourceField } from 'services/streamInsightsQuery';

const Wrapper = styled.div`
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid var(--color-border-secondary);
`;

// The "Sources" eyebrow doubles as the collapse toggle.
const ToggleHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-secondary);
`;

const Chevron = styled.div<{ $expanded: boolean }>`
  display: flex;
  transition: transform 120ms ease;
  transform: rotate(${(props) => (props.$expanded ? '0deg' : '-90deg')});
`;

const Body = styled.div`
  margin-top: 8px;
`;

const Row = styled.div<{ $focused?: boolean }>`
  & + & {
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px solid var(--color-border-secondary);
  }

  border-radius: 4px;
  transition: background-color 400ms ease;
  background-color: ${(props) =>
    props.$focused ? 'var(--color-bg-tertiary)' : 'transparent'};
`;

// The "[n]" badge on a source row, mirroring the inline marker in the prose.
const CiteBadge = styled.span`
  color: var(--color-text-link);
  font-weight: 600;
  margin-right: 4px;
`;

const Line = styled.div`
  & + & {
    margin-top: 2px;
  }
`;

const Label = styled.span`
  color: var(--color-text-secondary);
`;

const MetricValue = styled.span`
  color: var(--color-text-primary);
`;

const MetaValue = styled.span`
  color: var(--color-text-secondary);
`;

const FilterPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 4px;
  background-color: var(--color-bg-tertiary);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border-secondary);
`;

// Lineage (ENG-695 Tier 1): render each metric field with the raw source-system
// column it derives from, e.g. "distance ← LOADED_MILES". Falls back to just the
// field name when the derivation catalogue doesn't cover it.
const describeSourceFields = (fields: AnswerSourceField[]): string =>
  fields
    .map((f) => (f.sourceColumn ? `${f.field} ← ${f.sourceColumn}` : f.field))
    .join(', ');

const describeScope = (source: AnswerSource): string => {
  const parts = [`${source.startDate} → ${source.endDate}`];
  if (source.dateField) {
    parts.push(`by ${source.dateField}`);
  }
  if (source.breakdownField) {
    parts.push(`broken down by ${source.breakdownField}`);
  }
  if (source.trendByCalendarInterval) {
    parts.push(`trended by ${source.trendByCalendarInterval}`);
  }
  return parts.join(' · ');
};

// A labelled line: "<Label>: <value>". Keeps the label muted and the value
// styled per the row type so the metric name stands out from its metadata.
const LabelledLine = ({
  label,
  value,
  emphasis,
  badge,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
  // The source's citation number, rendered as a "[n]" badge before the label so
  // the footer row lines up with the inline marker in the prose.
  badge?: number;
}) => {
  const Value = emphasis ? MetricValue : MetaValue;
  return (
    <Line>
      <Typography.Body type="Body 12">
        {badge != null && <CiteBadge>{`[${badge}]`}</CiteBadge>}
        <Label>{`${label}: `}</Label>
        <Value>{value}</Value>
      </Typography.Body>
    </Line>
  );
};

// Auditable "source" footer under an answer: the metric, dataset, date range and
// filters of each aggregate_metric query the answer is built on. These come from
// the ACTUAL tool result (not the model's prose), so they are the ground truth
// for how a number was produced. Collapsed by default — click to expand, or an
// inline [n] citation marker in the prose can open and focus its row.
//
// `expanded`/`onToggle`/`focusedIndex` are optional: when the answer body drives
// them (to honour a citation click) the footer is controlled; otherwise it keeps
// its own collapsed-by-default state.
const InsightsMessageSource = React.forwardRef<
  HTMLDivElement,
  {
    sources: AnswerSource[];
    expanded?: boolean;
    onToggle?: (expanded: boolean) => void;
    focusedIndex?: number | null;
  }
>(({ sources, expanded, onToggle, focusedIndex }, ref) => {
  const [internalExpanded, setInternalExpanded] = useState<boolean>(false);
  const isControlled = expanded !== undefined;
  const isExpanded = isControlled ? expanded : internalExpanded;

  const toggle = () => {
    if (isControlled) {
      onToggle?.(!isExpanded);
    } else {
      setInternalExpanded((prev) => !prev);
    }
  };

  if (!sources.length) {
    return null;
  }
  return (
    <Wrapper ref={ref} data-testid="insights-message-source">
      <ToggleHeader
        type="button"
        onClick={toggle}
        aria-expanded={isExpanded}
        data-testid="insights-source-toggle"
      >
        <Chevron $expanded={!!isExpanded}>
          <Icon
            icon="chevron-down"
            width={12}
            height={12}
            color="var(--color-text-secondary)"
          />
        </Chevron>
        <Typography.Body type="Body 12">
          {`${sources.length === 1 ? 'Source' : 'Sources'} (${sources.length})`}
        </Typography.Body>
      </ToggleHeader>
      {isExpanded && (
        <Body>
          {sources.map((source, index) => (
            <Row
              key={`${source.metricId}-${index}`}
              $focused={
                focusedIndex != null && focusedIndex === source.citationIndex
              }
            >
              <LabelledLine
                label="Metric"
                value={source.metricName ?? source.metricId}
                emphasis
                badge={source.citationIndex}
              />
              {source.dataset && (
                <LabelledLine label="Dataset" value={source.dataset} />
              )}
              <LabelledLine label="Range" value={describeScope(source)} />
              {!!source.filters?.length && (
                <FilterPills>
                  {source.filters.map((filter, filterIndex) => (
                    <Pill key={`${filter.field}-${filterIndex}`}>
                      <Typography.Body type="Body 12">
                        {`${filter.field} ${filter.operator} ${filter.values.join(', ')}`}
                      </Typography.Body>
                    </Pill>
                  ))}
                </FilterPills>
              )}
              {/* Lineage (ENG-695 Tier 1): where the number comes from. */}
              {!!source.fields?.length && (
                <LabelledLine
                  label="Fields"
                  value={describeSourceFields(source.fields)}
                />
              )}
              {!!source.compoundExpression && (
                <LabelledLine
                  label="Formula"
                  value={source.compoundExpression}
                />
              )}
              {!!source.asOf && (
                <Line>
                  <Typography.Body type="Body 12">
                    <MetaValue>
                      {`Data as of ${moment.utc(source.asOf).fromNow()}`}
                    </MetaValue>
                  </Typography.Body>
                </Line>
              )}
            </Row>
          ))}
        </Body>
      )}
    </Wrapper>
  );
});

InsightsMessageSource.displayName = 'InsightsMessageSource';

export default InsightsMessageSource;
